// Manual rendering of videos (for mobile devices without WebCodec support).
// Manual rendering always uses low res videos, if available.

import type CreativeEngine from "@cesdk/engine";
import type {DesignBlockId} from "@cesdk/cesdk-js";
import {
    BLOCK_TRIM_LENGTH_META_KEY,
    BLOCK_TRIM_OFFSET_META_KEY, BLOCK_UUID_META_KEY,
    BLOCK_VIDEO_LOWRES_URI_META_KEY,
    BLOCK_VIDEO_POSTER_URI_META_KEY
} from "../constants.ts";
import {isMobile, supportsWebCodecs} from "../utils/browser.ts";
import {
    getAbsoluteStartOnPage,
    getDuration,
    getPageVideoBlocks, isVideoBlockHasSound, isVideoBlockMuted,
    removeVideoFill,
    swapVideoFillWithImage
} from "../utils/slideUtilities.ts";
import type {VirtualizedSlideInfo} from "../state/virtualizedSlide.ts";

// testing parameters
const ENABLED:boolean = true;
const TEST_ALWAYS_REPLACE:boolean = false;

// behaviour
const DEFAULT_PRELOAD_SECONDS: number | null = 3;
const SUPPORTED_VIDEO_FORMATS = ["mp4"];
const IGNORE_VIDEO_FORMATS = ["gif"];

// state
type RenderedVideo = {
    url: string;
    uuid: string | undefined;
    videoElement: HTMLVideoElement | null;
    canvas: HTMLCanvasElement | null;
    context: CanvasRenderingContext2D | null;
    blockId: DesignBlockId | null;
    fillId: DesignBlockId | null;

    frameCallback: number | null;
    canvasDirty: boolean;

    // debugging
    mobileInteraction: boolean;     // has this video been pre-interacted with on mobile

    // playback properties
    start: number;
    end: number;
    speed: number;
    volume: number;
    trimOffset: number;
    trimLength: number;

    // playback status
    starting?: boolean;
    startPromise?: Promise<void>;
    started: boolean;
    finished: boolean;
};
let customRenderVideos: Map<number, RenderedVideo[]> = new Map<number, RenderedVideo[]>();

let _useManualRendering:boolean | undefined;
let running:boolean = true;
let isPaused:boolean = false;
let lastTime:number = -1;
let tickFunction:number = 0;
let currentSlideId: number = -1;
let currentPageId: DesignBlockId | undefined;
let ceEngine: CreativeEngine | undefined;

export function useManualRendering(): boolean
{
    if (_useManualRendering !== undefined)
        return _useManualRendering;

    if (!ENABLED) {
        _useManualRendering = false;
        return _useManualRendering;
    }

    const params = new URLSearchParams(window.location.search);
    const hasVidRender = params.has('vidrender');
    if (hasVidRender) {
        _useManualRendering = true;
        return _useManualRendering;
    }

    if (TEST_ALWAYS_REPLACE) {
        _useManualRendering = true;
        return _useManualRendering;
    }

    if (isMobile() && !supportsWebCodecs()) {
        console.log("Detected no video support. Using manual video rendering.");
        _useManualRendering = true;
        return _useManualRendering;
    }

    _useManualRendering = false;
    return _useManualRendering;
}



/**
 * Will pre-create videos that need audio on mobile when we perform manual rendering.
 * A call to firstInteraction() must follow during a user interaction (click) to activate these videos for autoplay
 * later.
 */
export async function preloadVideosForMobile(engine: CreativeEngine, slides: VirtualizedSlideInfo[]): Promise<void>
{
    if (!useManualRendering())
        return;

    // temporarily construct each slide, find non-muted videos, and pre-create them
    for (const slide of slides)
    {
        const loadList: RenderedVideo[] = [];

        if (!slide.slideData)
            continue;

        // don't pre-create videos on intro slide (no audio on slide 0)
        if (slide.slideIndex === 0)
            continue;

        // temporarily build slide
        const pageBlocks: DesignBlockId[] = await engine.block.loadFromString(slide.slideData);
        if (!Array.isArray(pageBlocks) || pageBlocks.length === 0)
            continue;

        const pageId = pageBlocks[0];

        // get video blocks with sound, pre-create video elements
        const videos: DesignBlockId[] = getPageVideoBlocks(engine, pageId, SUPPORTED_VIDEO_FORMATS);
        const audioVideos = videos.filter(id => isVideoBlockHasSound(engine, id));

        for (const id of audioVideos)
        {
            const video = blockToVideo(engine, pageId, id);
            video.blockId = null;
            video.mobileInteraction = true;

            loadList.push(video);
        }

        // destroy temporary page
        engine.block.destroy(pageId);

        // create the videos.
        for (const video of loadList) {
            // create element, start pre-loading video data
            try {
                console.log("XXXX mobile preload, slide: {}, url: {}", slide.id, video.url);

                video.videoElement = await preloadVideoStream(video, DEFAULT_PRELOAD_SECONDS);
            } catch (err) {
                console.warn("Failed to preload video: {}", video.url, err);
                continue;
            }

            // add video to master list
            if (!customRenderVideos.has(slide.id))
                customRenderVideos.set(slide.id, []);
            customRenderVideos.get(slide.id)?.push(video);
        }
    }
}

/**
 * Preload a slide's videos for manual rendering.
 * Removes video block fills so that ce.sdk doesn't preload unneeded files.
 * Will wait until videos are loaded before returning.
 * Call when a slide is loading (before forceLoadResources() is called).
 * Returns true if manual rendering is enabled or the videos have been otherwise removed (i.e., caller shouldn't do any
 * other video replacements).
 */
export async function initSlideVideo(engine: CreativeEngine, pageId: DesignBlockId, slideId: number, introSlide: boolean): Promise<boolean>
{
    if (!useManualRendering())
        return false;

    ceEngine = engine;

    // replace video blocks with our own render targets (or poster images)
    const blocks: DesignBlockId[] = getPageVideoBlocks(engine, pageId, SUPPORTED_VIDEO_FORMATS);

    // custom video rendering
    if (ENABLED)
    {
        console.log("XXX Initing slide {}", pageId);

        // load video files and remove original fills from page (load before returning)
        await loadSlideVideos(engine, pageId, slideId, introSlide, blocks);
    }
    else
    {
        // fallback to poster image: replace with poster or remove video fill (worst case)
        for (const id of blocks) {

            if (!engine.block.hasMetadata(id, BLOCK_VIDEO_POSTER_URI_META_KEY)) {
                // no alternate poster available, just remove the fill (keep the block incase it's a click target)
                removeVideoFill(engine, id);
                continue;
            }

            const posterUri = engine.block.getMetadata(id, BLOCK_VIDEO_POSTER_URI_META_KEY);
            if (posterUri) {
                swapVideoFillWithImage(engine, id, posterUri);
            }
        }
    }

    return true;
}

/**
 * Trigger the first interaction (i.e. clicking the intro slide).
 * This allows us to capture video/audio autoplay permission by pre-creating all audio videos for the presentation.
 */
export async function firstInteraction(): Promise<void>
{
    const tasks: Promise<void>[] = [];

    // kick all audio videos
    for (const [, videos] of customRenderVideos)
    {
        for (const video of videos)
        {
            const el = video.videoElement;

            // only "kick" videos that have audio
            if (!el || video.volume === 0)
                continue;

            tasks.push((async () =>
            {
                try
                {
                    // tiny audible volume so iOS treats it as "audio playback" (still basically silent)
                    const originalVolume = video.volume;

                    el.volume = 0.01;

                    // IMPORTANT: await play, then pause, then restore volume
                    await el.play();

                    // pause is sync, but awaiting a microtask helps ensure the play promise resolves cleanly
                    el.pause();
                    await Promise.resolve();

                    el.volume = originalVolume;

                    console.log("XXX - mobile - first interaction - play/pausing video {}", video.blockId);
                }
                catch
                {
                    // swallow: NotAllowedError / AbortError / etc.
                    try { el.pause(); } catch {}
                    try { el.volume = video.volume; } catch {}
                }
            })());
        }
    }

    // run concurrently; don't return until they're all done
    await Promise.allSettled(tasks);
}

/**
 * Start rendering videos for a visible slide (monitoring for them to become visible).
 * Called by the player when a slide starts playing.
 */
export async function slideStarted(pageId: DesignBlockId, slideId: number, slideRestart: boolean = false)
{
    // start tracking playback, render videos as video blocks are visible

    if (!useManualRendering())
        return;

    if (!slideRestart && (currentPageId === pageId || currentSlideId === slideId))
        return;

    currentPageId = pageId;
    currentSlideId = slideId;

    if (!ceEngine)
        return;

    // re-create non-audio videos if this slide is being replayed
    //if (forceRestart) {
    //    // re-create non-audio videos (if any)
    //    await initSlideVideo(ceEngine, pageId, slideId, false);
    //}

    // check for no videos - don't need to run for this slide
    if (!customRenderVideos.get(slideId)?.length)
        return;

    // start
    running = true;
    lastTime = -1;
    tick();
}

/**
 * The current slide is about to be removed (no longer the current slide).
 * Call even if the slide has never started (ex. pre-cache occurred)
 * Stops playing any videos, remove from memory.
 * Must be called before the next slide's startSlide() call.
 */
export function clearSlide(slideId: number | undefined, resetSlide: boolean = false)
{
    if (!useManualRendering())
        return;

    console.log("XXX clearing slide {}", slideId);

    if (currentSlideId === slideId || resetSlide) {
        running = false;
        lastTime = -1;
        cancelAnimationFrame(tickFunction);
    }

    const currentSlideVideos = customRenderVideos.get(slideId ?? -1);
    if (!currentSlideVideos)
        return;

    for (let i = currentSlideVideos.length - 1; i >= 0; i--)
    {
        const video = currentSlideVideos[i];

        // handle videos with sound
        if (video.volume !== 0) {
            if (video.videoElement && !video.videoElement.paused)
                video.videoElement.pause();
            if (resetSlide && video.videoElement)
                video.videoElement.currentTime = video.start;
            if (video.frameCallback && video.videoElement) {
                video.videoElement.cancelVideoFrameCallback(video.frameCallback);
                video.frameCallback = null;
            }
            video.starting = false;
            video.started = false;
            video.finished = false;

            continue; // don't release sound videos on mobile (to keep interaction permission)
        }

        if (!resetSlide) {
            // normal slide change - release non-audio video
            releaseVideo(video);
            currentSlideVideos.splice(i, 1);
        }
        else {
            // restarting same slide - rewind video to original position
            if (video.videoElement && !video.videoElement.paused)
                video.videoElement.pause();
            if (video.videoElement)
                video.videoElement.currentTime = video.start;
            if (video.frameCallback && video.videoElement) {
                video.videoElement.cancelVideoFrameCallback(video.frameCallback);
                video.frameCallback = null;
            }
            video.starting = false;
            video.started = false;
            video.finished = false;
        }
    }
}

function releaseVideo(video: RenderedVideo)
{
    console.log("XXX releaseVideo slide {}", video.url);

    const element = video.videoElement;
    if (!element)
        return;

    if (video.frameCallback)
        element.cancelVideoFrameCallback(video.frameCallback);

    try { element.pause(); } catch {}
    element.removeAttribute("src");
    try { element.load(); } catch {}

    if (element.parentNode)
        element.parentNode.removeChild(element);
}

function blockToVideo(engine: CreativeEngine, pageId: DesignBlockId, blockId: DesignBlockId): RenderedVideo
{
    // get fill/video urls
    const originalVideoFillId = engine.block.getFill(blockId);
    let url: string = engine.block.getString(originalVideoFillId, "fill/video/fileURI");

    // get the best video url (prefer low-res)
    if (engine.block.hasMetadata(blockId, BLOCK_VIDEO_LOWRES_URI_META_KEY)) {
        const lowResUri = engine.block.getMetadata(blockId, BLOCK_VIDEO_LOWRES_URI_META_KEY);
        if (lowResUri)
            url = lowResUri
    }

    // get video block details
    const start = getAbsoluteStartOnPage(engine, blockId, pageId);
    const dur = getDuration(engine, blockId);
    const speed = engine.block.getPlaybackSpeed(originalVideoFillId);
    const volume = isVideoBlockMuted(engine, blockId) ? 0 : engine.block.getVolume(originalVideoFillId);

    let uuid: string | undefined;
    let trimOffset = 0;
    let trimLength = 0;
    try {
        if (engine.block.hasMetadata(blockId, BLOCK_UUID_META_KEY))
            uuid = engine.block.getMetadata(blockId, BLOCK_UUID_META_KEY);
        if (engine.block.hasMetadata(blockId, BLOCK_TRIM_OFFSET_META_KEY))
            trimOffset = Number(engine.block.getMetadata(blockId, BLOCK_TRIM_OFFSET_META_KEY));
        if (engine.block.hasMetadata(blockId, BLOCK_TRIM_LENGTH_META_KEY))
            trimLength = Number(engine.block.getMetadata(blockId, BLOCK_TRIM_LENGTH_META_KEY));
    }
    catch(err) {
        console.error("Error getting video block metadata: ", err);
    }

    // remove the original video fill - must be done before new video starts to load (or ce.sdk will start loading
    // as well)
    removeVideoFill(engine, blockId);

    // create the video details
    const video: RenderedVideo = {
        url,
        uuid: uuid,
        videoElement: null,
        canvas: null,
        context: null,
        blockId: blockId,
        fillId: null,

        canvasDirty: false,
        frameCallback: null,

        mobileInteraction: false,

        started: false,
        finished: false,

        start: start,
        end: start + dur,
        speed: speed,
        volume: volume,
        trimOffset: trimOffset,
        trimLength: trimLength,
    };

    return video;
}

async function loadSlideVideos(engine: CreativeEngine, pageId: DesignBlockId, slideId: number, introSlide: boolean, videoBlocks: DesignBlockId[]): Promise<void>
{
    const slideVideos: RenderedVideo[] = [];

    //debugger;

    // get all video elements, remove fills to prevent ce.sdk loading (must be done before any async calls)
    for (const id of videoBlocks) {
        // create the video (and remove the original fill)
        const video = blockToVideo(engine, pageId, id);
        if (introSlide)
            video.volume = 0;

        // check if this video already exists (pre-created/pre-interacted sound video).
        // try to find the video using the uuid. fall-back to finding the first video with the same url.
        const pageVideos = customRenderVideos.get(slideId);
        if (pageVideos) {
            let existingVideo = pageVideos.find(v => v.uuid && v.uuid === video.uuid);
            if (!existingVideo && !video.uuid)
                existingVideo = pageVideos.find(v => v.url && !v.uuid && v.url === video.url);

            if (existingVideo && existingVideo.videoElement) {
                existingVideo.blockId = id;
                connectVideoToBlock(engine, existingVideo, existingVideo.videoElement);
                continue;
            }
        }

        // new video, add to list to create after initial enumeration
        slideVideos.push(video);
    }

    // create the HTMLVideoElements, start pre-loading video data
    for (const video of slideVideos)
    {
        // we didn't find an existing video element for this video.
        // create it, pre-load it, and connect it to the block.

        try {
            video.videoElement = await preloadVideoStream(video, DEFAULT_PRELOAD_SECONDS);
        } catch (err) {
            console.warn("Failed to preload video: {}", video.url, err);
            continue;
        }

        // add video to master list
        if (!customRenderVideos.has(slideId))
            customRenderVideos.set(slideId, []);
        customRenderVideos.get(slideId)?.push(video);

        // connect for playback
        connectVideoToBlock(engine, video, video.videoElement);
    }

    console.log("XXX pre-created {} slide videos for page {}", slideVideos.length, pageId);
}

function connectVideoToBlock(engine: CreativeEngine, video: RenderedVideo, videoElement: HTMLVideoElement)
{
    // old fill will already have been removed by blockToVideo

    if (!video.blockId) {
        console.error("Missing video blockID", video.url);
        return;
    }

    const canvas = document.createElement("canvas");

    // add the new fill for the video block (pixel rendering)
    const pixelFillId = engine.block.createFill("pixelStream");
    engine.block.setFill(video.blockId, pixelFillId);

    console.log("XXX - connected video {} to block {}", video.url, video.blockId);

    // size canvas to video frame
    canvas.width = videoElement.videoWidth || 800;
    canvas.height = videoElement.videoHeight || 450;

    const context = canvas.getContext("2d", {
        alpha: false,
        desynchronized: true,
        willReadFrequently: false
    });

    video.canvas = canvas;
    video.context = context;
    video.fillId = pixelFillId;
}

function frameCallback(video: RenderedVideo, now: DOMHighResTimeStamp, metadata: VideoFrameCallbackMetadata)
{
    if (!video || video.context == null || video.canvas == null || video.finished) {
        video.frameCallback = null;
        return;
    }

    if (video.fillId && video.videoElement && !video.videoElement?.paused)
    {
        try {
            // Copy current frame
            video.context.clearRect(0, 0, video.canvas.width, video.canvas.height);
            video.context.drawImage(video.videoElement, 0, 0, video.canvas.width, video.canvas.height);
            video.canvasDirty = true;

            // canvas pixels copied into block occurs in tick
        } catch (err) {
            console.warn("Failed to draw video frame:", video.url, err);
        }
    }
    video.frameCallback = video.videoElement?.requestVideoFrameCallback((now, metadata) => { frameCallback(video, now, metadata);}) ?? null;
}

export function pause() {
    // prevent double call
    if (!running || isPaused)
        return;

    // state
    isPaused = true;

    // pause any playing videos
    if (!currentPageId)
        return;

    const currentSlideVideos = customRenderVideos.get(currentSlideId);
    if (!currentSlideVideos)
        return;

    for (const video of currentSlideVideos)
    {
        if (video.started && !video.finished && video.videoElement && !video.videoElement.paused)
        {
            video.videoElement.pause();
        }
    }
}

export function unPause() {
    // prevent double call
    if (!running || !isPaused)
        return;

    // state
    isPaused = false;

    // resume any previously paused videos
    if (!currentPageId)
        return;

    const currentSlideVideos = customRenderVideos.get(currentSlideId);
    if (!currentSlideVideos)
        return;

    for (const video of currentSlideVideos)
    {
        if (video.started && !video.finished && video.videoElement?.paused)
        {
            void video.videoElement.play();
        }
    }
}

function tick()
{
    if (!running || !currentPageId)
        return;

    const currentSlideVideos = customRenderVideos.get(currentSlideId);
    if (!currentSlideVideos?.length)
        return;

    // get the current playback time
    const curTime = ceEngine?.block.supportsPlaybackTime(currentPageId) ? ceEngine.block.getPlaybackTime(currentPageId) : 0;

    //console.log("XXX Tick: {}, video count: {}", curTime, currentSlideVideos.length)

    // draw each tracked video's frame
    for (let i = 0; i < currentSlideVideos.length; i++)
    {
        const video = currentSlideVideos[i];
        const element = video.videoElement;
        if (!element || !video.context || !video.canvas || !video.fillId) continue;

        // start playing when crossing each video start boundary
        if (!video.started && !video.finished && lastTime < video.start && curTime >= video.start) {
            //if (video.url.includes("24e7")) {
            //    console.log(`XXX - video: {}, paused: {}, current time: {}, video.trimOffset: {}`, video.url, video.videoElement?.paused, video.videoElement?.currentTime, video.trimOffset);
            //}

            // set here on purpose (to prevent multiple overlapping start attempts)
            video.started = true;

            if (!video.starting) { // prevent multiple overlapping start attempts
                video.starting = true;

                console.log(`XXX - starting video.url: {}, volume {}`, video.url, video.volume, video);

                video.startPromise = seekAndPlayAtOffset(element, video.trimOffset)
                    .catch(err => {
                        console.error("Failed to seek/play video: {}", video.url, err);
                    })
                    .finally(() => {
                        console.log("XXX Successfully seeked video: {}, offset {}", video.url, video.trimOffset);
                        video.starting = false;

                        if (!video.frameCallback)
                            video.frameCallback = video.videoElement?.requestVideoFrameCallback((now, metadata) => { frameCallback(video, now, metadata);}) ?? null;
                    });
            }
        }

        // only draw if we have something to draw
        //if (element.readyState >= 2 /* HAVE_CURRENT_DATA */ && video.started && !video.finished && !element.ended) {
        if (video.started && !video.finished && !element.ended) {
            //if (video.url.includes("24e7")) {
            //    console.log("XXX - drawing frame: {}, paused: {}, current time: {}, element size: {}x{}", video.url, video.videoElement?.paused, video.videoElement?.currentTime, element.videoWidth, element.videoHeight);
            //}

            // Push canvas pixels into CE.SDK.
            // Frame was drawn in video's frame callback.
            if (video.canvasDirty) {
                ceEngine?.block.setNativePixelBuffer(video.fillId, video.canvas);
                video.canvasDirty = false;
            }
        }

        // check if video has ended (end of video playback)
        if (video.started && !video.finished && element.ended) {
            // don't release videos in case of slide restart
            element.pause();
            video.finished = true;
            console.log(`XXX - video ended. url: {}, volume {}`, video.url, video.volume, video);
        }

        // check if we've passed the end of the video element, remove
        if (video.started && !video.finished && curTime >= video.end) {
            // don't release videos in case of slide restart
            element.pause();
            video.finished = true;
            console.log(`XXX - video passed end. url: {}, volume {}`, video.url, video.volume, video);
        }
    }


    lastTime = curTime;
    tickFunction = requestAnimationFrame(tick);
}

async function preloadVideoStream(
    videoData: RenderedVideo,
    bufferSeconds: number | null,
): Promise<HTMLVideoElement>
{
    const video = document.createElement('video');
    video.preload = 'auto';
    video.crossOrigin = 'anonymous';
    video.src = videoData.url;
    video.loop = false;
    video.muted = videoData.volume === 0;
    if (videoData.volume > 0 && videoData.volume != 1)
        video.volume = videoData.volume;

    video.playsInline = true;    // iOS: avoid fullscreen
    video.style.position = "fixed";
    video.style.left = "-99999px";
    video.style.top = "0";
    video.style.width = "1px";
    video.style.height = "1px";
    video.style.opacity = "0";
    video.style.pointerEvents = "none";
    document.body.appendChild(video); // may not be required, even on iOS 18

    console.log("XXX created video element {}", videoData.url);

    await new Promise<void>((resolve, reject) => {
        const HARD_TIMEOUT_MS = 1500;

        const hardTimeout = window.setTimeout(() => {
            console.warn("XXX preload timeout, continuing {}", videoData.url);
            cleanup();
            resolve();
        }, HARD_TIMEOUT_MS);

        const onError = () => {
            cleanup();
            reject(new Error("Video preload failed"));
        };

        const cleanup = () => {
            window.clearTimeout(hardTimeout);
            video.removeEventListener("progress", checkBuffered);
            //video.removeEventListener("timeupdate", checkBuffered);
            video.removeEventListener("loadedmetadata", onLoadedMetadata);
            video.removeEventListener("canplaythrough", onCanPlayThrough);
            video.removeEventListener("error", onError);
        };

        const onLoadedMetadata = async () => {
            if (videoData.speed !== 1)
                video.playbackRate = videoData.speed;

            if (videoData.trimOffset > 0) {
                const seeked = waitEvent(video, "seeked");
                video.currentTime = videoData.trimOffset;
                await Promise.race([seeked, new Promise<void>(r => setTimeout(r, 750))]);
            }

            if (bufferSeconds !== null)
                checkBuffered();
        };

        const onCanPlayThrough = () => {
            console.log("XXX video canplaythrough {}", videoData.url);
            cleanup();
            resolve();
        };

        const BUFFER_EPS = 0.4; // ~400ms wiggle room

        const checkBuffered = () => {
            if (video.buffered.length === 0) return;

            const base = videoData.trimOffset > 0 ? videoData.trimOffset : 0;
            const required = Math.min(bufferSeconds!, video.duration || bufferSeconds!);
            const requiredEnd = base + required;

            for (let i = 0; i < video.buffered.length; i++) {
                const start = video.buffered.start(i);
                const end = video.buffered.end(i);

                // we only care about a range that starts near/at our base and extends far enough
                if (start <= base + 0.25 && end + BUFFER_EPS >= requiredEnd) {
                    console.log("XXX video pre-buffered {}", videoData.url, end, requiredEnd);
                    cleanup();
                    resolve();
                    return;
                }
            }
        };

        video.addEventListener('error', onError);
        video.addEventListener('loadedmetadata', onLoadedMetadata);

        if (bufferSeconds === null) {
            // Simple mode: just wait until browser says it's ready
            video.addEventListener('canplaythrough', onCanPlayThrough);
        } else {
            video.addEventListener('progress', checkBuffered);
            //video.addEventListener('timeupdate', checkBuffered);
        }

        video.load();
    });

    return video;
}

function waitEvent(el: EventTarget, name: string) {
    return new Promise<void>(res => el.addEventListener(name, () => res(), { once: true }));
}

async function seekAndPlayAtOffset(el: HTMLVideoElement, offset: number) {
    // wait until currentTime is meaningful
    if (el.readyState < 1) await waitEvent(el, "loadedmetadata");

    // only seek if needed (avoid seek spam)
    if (offset !== 0 && Math.abs(el.currentTime - offset) > 0.05) {
        const seeked = waitEvent(el, "seeked");
        el.currentTime = offset;
        // don't deadlock if Safari doesn't fire seeked
        await Promise.race([seeked, new Promise<void>(r => setTimeout(r, 750))]);
    }
    else if (offset === 0 && el.currentTime !== 0) {
        const seeked = waitEvent(el, "seeked");
        el.currentTime = 0;
        await Promise.race([seeked, new Promise<void>(r => setTimeout(r, 750))]);
    }

    await el.play(); // if NotAllowedError, let caller handle interaction gating
}
